"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import {
  Search,
  MapPin,
  Check,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  X,
  Phone,
  RotateCcw,
  Navigation,
  Clock,
  User,
  Globe,
  Star,
  AlertCircle,
  FileText,
  Car
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Dynamically import the map to avoid SSR window issues
const DealersMap = dynamic(() => import("./components/DealersMap"), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-gray-100 flex items-center justify-center"><MapPin className="w-8 h-8 text-gray-300 animate-pulse" /></div>
});

const cityToProvince = {
  "تهران": "تهران",
  "مشهد": "خراسان رضوی",
  "قزوین": "قزوین",
  "زاهدان": "سیستان و بلوچستان",
  "گرگان": "گلستان",
  "قم": "قم",
  "یزد": "یزد",
  "بندر عباس": "هرمزگان",
  "کرمان": "کرمان",
  "بابلسر": "مازندران",
};

function CustomCheckbox({ checked, onChange, label, count }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-center justify-between w-full text-right py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group cursor-pointer"
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-200 ${checked
              ? "bg-blue-950 border-blue-950 text-white shadow-sm shadow-blue-950/25 scale-105"
              : "border-gray-200 bg-white group-hover:border-gray-300"
            }`}
        >
          {checked && <Check className="w-3.5 h-3.5 stroke-[3] animate-scale-in" />}
        </div>
        <span className={`text-sm transition-colors duration-200 ${checked ? "text-gray-900 font-semibold" : "text-gray-600 font-medium group-hover:text-gray-800"
          }`}>
          {label}
        </span>
      </div>
      {count !== undefined && (
        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition-all duration-200 latinNumbers ${checked ? "bg-blue-50 text-blue-950 font-semibold" : "bg-gray-100 text-gray-400 group-hover:bg-gray-200"
          }`}>
          {count}
        </span>
      )}
    </button>
  );
}

function FilterGroup({ title, children, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-100 py-4.5 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between w-full text-right font-bold text-gray-800 pb-2.5 cursor-pointer hover:text-blue-950 transition-colors"
      >
        <span className="text-base">{title}</span>
        <div className={`p-1 rounded-lg bg-gray-50 text-gray-400 hover:text-gray-600 transition-all duration-200 ${isOpen ? "rotate-180 bg-blue-50 text-blue-950" : ""}`}>
          <ChevronDown className="w-4 h-4" />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-2 space-y-1">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const DealerDetailsExpanded = ({ id }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`https://www.barsavosh.co/api/dealer/detail/${id}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          setBranches(data);
        }
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
      });
  }, [id]);

  if (loading) return <div className="p-4 text-center text-gray-400 font-bold text-xs bg-gray-50/50">درحال دریافت اطلاعات...</div>;
  if (!branches || branches.length === 0) return null;

  return (
    <div className="bg-gray-50/80 border-t border-gray-100 flex flex-col">
      {branches.map((branch, index) => (
        <div key={branch.id || index} className={`p-4 space-y-3 ${index > 0 ? "border-t-2 border-gray-200 border-dashed" : ""}`}>
          
          <div className="flex items-center justify-between mb-1">
            <span className="font-extrabold text-[13px] text-gray-900">{branch.name}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${branch.is_sale ? "bg-blue-100 text-blue-700" : "bg-[#CAAA6D]/15 text-[#8F6F33] border border-[#CAAA6D]/35"}`}>
               {branch.is_sale ? "فروش" : "خدمات پس از فروش"}
            </span>
          </div>

          {branch.address && (
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2.5 text-xs text-gray-700 font-medium">
                <MapPin className="w-4 h-4 shrink-0 text-gray-400 mt-0.5" />
                <span className="leading-relaxed">{branch.address}</span>
              </div>
              {(branch.google_map_location || branch.neshan_map_location || branch.description) && (
                <div className="flex items-center gap-2 pt-0.5">
                  <a
                    href={branch.google_map_location || (branch.description ? `https://www.google.com/maps/search/?api=1&query=${branch.description.replace('/', ',')}` : '#')}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-[#5281A8] hover:bg-[#3d617e] px-3 py-1.5 rounded-lg transition-colors shadow-xs"
                  >
                    <Navigation className="w-3.5 h-3.5 -scale-x-100" />
                    <span>مسیریابی به این واحد</span>
                  </a>
                  {branch.neshan_map_location && (
                    <a
                      href={branch.neshan_map_location}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-[11px] font-medium text-gray-600 hover:text-black bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      نشان
                    </a>
                  )}
                  {branch.balad_map_location && (
                    <a
                      href={branch.balad_map_location}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center text-[11px] font-medium text-gray-600 hover:text-black bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      بلد
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          
          {branch.phones && (
            <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 shrink-0 text-gray-400" />
                <span>شماره تماس:</span>
              </div>
              <span className="latinNumbers font-bold text-[13px]">{branch.phones}</span>
            </div>
          )}

          {branch.complaint_phones && (
            <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-2.5">
                <AlertCircle className="w-4 h-4 shrink-0 text-gray-400" />
                <span>شماره شکایت:</span>
              </div>
              <span className="latinNumbers font-bold text-[13px]">{branch.complaint_phones}</span>
            </div>
          )}
          
          {branch.manager_name && (
            <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-2.5">
                <User className="w-4 h-4 shrink-0 text-gray-400" />
                <span>مدیریت:</span>
              </div>
              <span>{branch.manager_name}</span>
            </div>
          )}

          {branch.customer_satisfaction_score && (
            <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-2.5">
                <Star className="w-4 h-4 shrink-0 text-gray-400" />
                <span>امتیاز رضایت مشتریان:</span>
              </div>
              <span className="latinNumbers font-bold text-[13px] bg-blue-50 text-blue-800 px-2 py-0.5 rounded">{branch.customer_satisfaction_score}</span>
            </div>
          )}

          {branch.isqi_grade && (
            <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
              <div className="flex items-center gap-2.5">
                <FileText className="w-4 h-4 shrink-0 text-gray-400" />
                <span>رتبه ISQI:</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold">{branch.isqi_grade}</span>
                {branch.isqi_file && (
                   <a href={branch.isqi_file} target="_blank" rel="noreferrer" className="bg-red-50 text-red-600 p-1 rounded hover:bg-red-100 transition-colors" title="دانلود گواهی">
                     <FileText className="w-3.5 h-3.5" />
                   </a>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-gray-700 font-medium">
            <div className="flex items-center gap-2.5">
              <Car className="w-4 h-4 shrink-0 text-gray-400" />
              <span>خودروهای تحت پوشش:</span>
            </div>
            <span>تمامی خودروهای برساوش</span>
          </div>
          
          {branch.normal_day_start_hour && (
            <div className="flex items-start gap-2.5 text-xs text-gray-700 font-medium">
                <Clock className="w-4 h-4 shrink-0 text-gray-400 mt-0.5" />
                <div className="flex flex-col gap-1.5 w-full">
                  <div className="flex justify-between items-center bg-white px-2.5 py-1.5 rounded border border-gray-100 shadow-sm" dir="rtl">
                     <span>شنبه تا چهارشنبه</span>
                     <div className="flex items-center gap-1.5 font-bold text-gray-800" dir="rtl">
                       <span className="latinNumbers">{branch.normal_day_start_hour}</span>
                       <span className="text-gray-400 font-normal text-xs">الی</span>
                       <span className="latinNumbers">{branch.normal_day_end_hour}</span>
                     </div>
                  </div>
                  {branch.thursday_start_hour && (
                    <div className="flex justify-between items-center bg-white px-2.5 py-1.5 rounded border border-gray-100 shadow-sm" dir="rtl">
                       <span>پنجشنبه</span>
                       <div className="flex items-center gap-1.5 font-bold text-gray-800" dir="rtl">
                         <span className="latinNumbers">{branch.thursday_start_hour}</span>
                         <span className="text-gray-400 font-normal text-xs">الی</span>
                         <span className="latinNumbers">{branch.thursday_end_hour}</span>
                       </div>
                    </div>
                  )}
                  {branch.friday_start_hour && branch.friday_end_hour && (
                    <div className="flex justify-between items-center bg-white px-2.5 py-1.5 rounded border border-gray-100 shadow-sm" dir="rtl">
                       <span>جمعه</span>
                       <div className="flex items-center gap-1.5 font-bold text-gray-800" dir="rtl">
                         <span className="latinNumbers">{branch.friday_start_hour}</span>
                         <span className="text-gray-400 font-normal text-xs">الی</span>
                         <span className="latinNumbers">{branch.friday_end_hour}</span>
                       </div>
                    </div>
                  )}
                </div>
            </div>
          )}
          
        </div>
      ))}
    </div>
  );
};

export default function DealersPage() {
  const [dealers, setDealers] = useState([]);

  // States
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [expandedDealers, setExpandedDealers] = useState({});

  // UI states
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ province: false, status: false });

  const dealerRefs = useRef({});

  const toggleDetails = (e, id) => {
    e.stopPropagation();
    setExpandedDealers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    async function fetchDealers() {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}dealer/list`);
        const data = await res.json();
        // Inject province logic
        const processedData = data.map(d => ({
          ...d,
          province: cityToProvince[d.city?.name] || d.city?.name || "سایر"
        }));
        setDealers(processedData);
      } catch (error) {
        console.log("Failed to load dealers", error);
      }
    }
    fetchDealers();
  }, []);

  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const handleProvinceToggle = (province) => {
    setSelectedProvinces(prev => prev.includes(province) ? prev.filter(p => p !== province) : [...prev, province]);
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };

  const handleClearAll = () => {
    setSelectedProvinces([]);
    setSelectedStatuses([]);
    setSearchQuery("");
  };

  const provinces = [...new Set(dealers.map(d => d.province).filter(Boolean))];

  const getDealerStatus = (dealer) => dealer.dealers?.[0]?.dealer_status || "فعال";

  const filteredDealers = dealers.filter((dealer) => {
    const status = getDealerStatus(dealer);
    const matchesSearch = searchQuery.trim() === "" ||
      dealer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.code?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.city?.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvince = selectedProvinces.length === 0 || selectedProvinces.includes(dealer.province);
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(status);
    return matchesSearch && matchesProvince && matchesStatus;
  });

  const getProvinceCount = (prov) => dealers.filter(d => d.province === prov).length;
  const getStatusCount = (stat) => dealers.filter(d => getDealerStatus(d) === stat).length;

  const handleDealerSelect = (id, autoExpand = true) => {
    setSelectedDealerId(id);
    if (autoExpand) {
      setExpandedDealers(prev => ({ ...prev, [id]: true }));
    }
    if (dealerRefs.current[id]) {
      dealerRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const FilterContent = () => (
    <div className="space-y-2">
      <FilterGroup title="استان" isOpen={expandedGroups.province} onToggle={() => toggleGroup("province")}>
        {provinces.map((prov) => (
          <CustomCheckbox
            key={prov}
            checked={selectedProvinces.includes(prov)}
            onChange={() => handleProvinceToggle(prov)}
            label={prov}
            count={getProvinceCount(prov)}
          />
        ))}
      </FilterGroup>

      <FilterGroup title="وضعیت نمایندگی" isOpen={expandedGroups.status} onToggle={() => toggleGroup("status")}>
        <CustomCheckbox
          checked={selectedStatuses.includes("فعال")}
          onChange={() => handleStatusToggle("فعال")}
          label="فعال"
          count={getStatusCount("فعال")}
        />
        <CustomCheckbox
          checked={selectedStatuses.includes("لغو شده")}
          onChange={() => handleStatusToggle("لغو شده")}
          label="لغو شده"
          count={getStatusCount("لغو شده")}
        />
      </FilterGroup>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row-reverse lg:items-start min-h-[100dvh] w-full bg-gray-50 pt-[100px] pb-10 px-4 lg:px-8 gap-6" dir="rtl">

      {/* Sidebar List (Left Side on Desktop) */}
      <aside className="w-full lg:w-[45%] flex flex-col bg-white border border-gray-100 shadow-xl rounded-2xl overflow-hidden z-20 shrink-0">

        {/* Header & Search (Sticky) */}
        <div className="p-5 border-b border-gray-100 bg-white shrink-0">
          <div className="mb-6 mt-2">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">نمایندگی‌ها</h1>
            <p className="text-sm font-medium text-gray-500 mt-2">یافتن نزدیک‌ترین مرکز برساوش به شما</p>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="جستجو در نام، شهر، استان یا کد..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-950 focus:border-transparent text-sm transition-all"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          <button
            onClick={() => setIsMobileDrawerOpen(true)}
            className="w-full mt-3 lg:hidden flex items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-800 font-bold py-2.5 px-4 rounded-xl text-sm transition-all shadow-sm"
          >
            <SlidersHorizontal className="w-4 h-4 text-blue-950" />
            <span>فیلترها {(selectedProvinces.length + selectedStatuses.length) > 0 ? `(${selectedProvinces.length + selectedStatuses.length})` : ''}</span>
          </button>
        </div>

        {/* Desktop Filters (Hidden on Mobile) */}
        <div className="hidden lg:block p-5 border-b border-gray-100 bg-white shrink-0">
          <FilterContent />
          {(selectedProvinces.length > 0 || selectedStatuses.length > 0 || searchQuery) && (
            <button onClick={handleClearAll} className="w-full mt-3 py-2 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-2">
              <RotateCcw className="w-3.5 h-3.5" />
              پاک کردن فیلترها
            </button>
          )}
        </div>

        {/* Dealer List */}
        <div className="flex-1 p-5 bg-gray-50">
          <div className="text-xs font-bold text-gray-400 mb-4">{filteredDealers.length} نمایندگی یافت شد</div>

          <div className="space-y-4">
            {filteredDealers.map((dealer) => {
              const status = getDealerStatus(dealer);
              const isSelected = selectedDealerId === dealer.id;

              const isExpanded = expandedDealers[dealer.id];

              return (
                <div
                  key={dealer.id}
                  ref={el => dealerRefs.current[dealer.id] = el}
                  onClick={() => handleDealerSelect(dealer.id)}
                  className={`border rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 flex flex-col ${isSelected ? "bg-blue-50/40 border-blue-950/40 ring-2 ring-blue-950/20 shadow-md" : "bg-white border-gray-100 shadow-sm hover:shadow-md hover:border-slate-300"
                    }`}
                >
                  {/* Top info section */}
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex justify-between items-start gap-1">
                      <h3 className="font-extrabold text-[15px] text-gray-900 leading-tight">{dealer.name}</h3>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${status === "لغو شده" ? "bg-red-50 text-red-700" : status === "در حال راه اندازی" ? "bg-yellow-50 text-yellow-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {status}
                      </span>
                    </div>
                    
                    <div className="flex flex-col gap-2.5">
                      <div className="flex items-center gap-2 text-xs text-gray-600 font-medium">
                        <MapPin className="w-4 h-4 text-blue-950 shrink-0" />
                        <span>{dealer.province}، {dealer.city?.name}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-600 font-medium">
                        <div className="w-4 h-4 rounded bg-gray-100 text-[10px] flex items-center justify-center font-bold text-gray-600 shrink-0">#</div>
                        <span>کد نمایندگی: <span className="latinNumbers font-bold">{dealer.code}</span></span>
                      </div>
                    </div>
                  </div>

                  {/* Accordion Toggle Button */}
                  <div className="px-4 pb-4">
                    <button 
                      onClick={(e) => toggleDetails(e, dealer.id)}
                      className="w-full bg-gray-100/70 hover:bg-gray-200/70 transition-colors text-gray-700 text-xs font-bold py-2.5 rounded-xl flex items-center justify-center gap-1.5"
                    >
                      {isExpanded ? (
                         <>
                           <ChevronUp className="w-4 h-4" />
                           بستن جزئیات
                         </>
                      ) : (
                         <>
                           <ChevronDown className="w-4 h-4" />
                           نمایش جزئیات بیشتر
                         </>
                      )}
                    </button>
                  </div>

                  {/* Expanded Content Area */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                         <DealerDetailsExpanded id={dealer.id} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}

            {filteredDealers.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-8 h-8 text-gray-300" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">نتیجه‌ای یافت نشد</h3>
                <p className="text-xs text-gray-500">فیلترها را تغییر دهید.</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Map Area (Left Side) */}
      <div className="w-full lg:w-[55%] sticky top-[100px] z-10 shrink-0 lg:shrink">
        <div className="w-full bg-gray-100 relative h-[50vh] lg:h-[calc(100vh-140px)] rounded-2xl overflow-hidden shadow-xl">
          <DealersMap
            dealers={filteredDealers}
            selectedDealerId={selectedDealerId}
            onMarkerClick={handleDealerSelect}
          />
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <AnimatePresence>
        {isMobileDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileDrawerOpen(false)}
              className="fixed inset-0 z-50 bg-black lg:hidden"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="fixed inset-x-0 bottom-0 z-50 h-[85vh] bg-white rounded-t-3xl shadow-2xl flex flex-col lg:hidden"
              dir="rtl"
            >
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <span className="font-extrabold text-gray-900 text-lg flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-blue-950" />
                  فیلترها
                </span>
                <button
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <FilterContent />
              </div>

              <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setIsMobileDrawerOpen(false)}
                  className="flex-1 bg-blue-950 text-white py-3 rounded-xl font-bold shadow-sm"
                >
                  اعمال فیلتر ({filteredDealers.length})
                </button>
                <button
                  onClick={handleClearAll}
                  className="bg-white border border-gray-200 text-red-600 py-3 px-4 rounded-xl font-bold"
                >
                  پاک کردن
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </div>
  );
}
